import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Ticket, Users } from 'lucide-react';
import type { EventItem } from '../api/types';
import WorkshopContactSection from '../components/home/WorkshopContactSection';


export default function Events() {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [filterType, setFilterType] = useState<'all' | 'market' | 'workshop'>('all');

  useEffect(() => {
    api
      .events()
      .then((data) => setEvents(data))
      .catch(console.error);
  }, []);

  const filteredEvents = useMemo(() => {
    const upcoming = events.filter((ev) => !!ev.startDate);
    return upcoming.filter((ev) => (filterType === 'all' ? true : ev.eventType === filterType));
  }, [events, filterType]);

  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    events.forEach((ev) => {
      if (ev.eventType) set.add(ev.eventType);
    });
    return Array.from(set);
  }, [events]);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#9CAF88]/20 to-[#FDF8F5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-serif text-5xl sm:text-6xl font-semibold text-[#3D3D3D] mb-4">Upcoming Events</h1>
            <p className="font-sans text-lg text-[#3D3D3D]/70 max-w-2xl mx-auto">Join us at local markets or dive deeper into your creative practice with our hands-on workshops</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filter Tabs - only show categories with items */}
        {filteredEvents.length === 0 ? null : (
          <div className="flex justify-center gap-3 mb-12">
            <button onClick={() => setFilterType('all')} className={`rounded-full font-sans px-4 py-2 ${filterType === 'all' ? 'bg-[#3D3D3D] text-white' : 'border border-gray-200 text-[#3D3D3D] hover:border-[#D4A5A5]'}`}>All Events</button>
            {availableTypes.includes('market') && (
              <button onClick={() => setFilterType('market')} className={`rounded-full font-sans px-4 py-2 ${filterType === 'market' ? 'bg-[#9CAF88] text-white' : 'border border-gray-200 text-[#3D3D3D] hover:border-[#9CAF88]'}`}>Market Days</button>
            )}
            {availableTypes.includes('workshop') && (
              <button onClick={() => setFilterType('workshop')} className={`rounded-full font-sans px-4 py-2 ${filterType === 'workshop' ? 'bg-[#C9A962] text-white' : 'border border-gray-200 text-[#3D3D3D] hover:border-[#C9A962]'}`}>Workshops</button>
            )}
          </div>
        )}

        {/* Events List */}
        {filteredEvents.length === 0 ? null /*(
          <div className="text-center py-20">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#3D3D3D] mb-2">No upcoming events</h3>
            <p className="font-sans text-[#3D3D3D]/60">Check back soon for new events!</p>
          </div>
        ) */ : (
          <div className="grid md:grid-cols-2 gap-8">
            {filteredEvents.map((event, index) => (
              <motion.div key={event.squareProductId} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 group">
                  <div className="relative">
                    <img src={event.imageUrls?.[0]} alt={event.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4">
                      <span className={`px-4 py-2 rounded-full font-sans text-sm font-medium ${event.eventType === 'workshop' ? 'bg-[#C9A962] text-white' : 'bg-[#9CAF88] text-white'}`}>{event.eventType === 'workshop' ? 'Workshop' : 'Market Day'}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-4 group-hover:text-[#D4A5A5] transition-colors">{event.title}</h3>
                    {event.description && (
                      <div
                        className="font-sans text-[#3D3D3D]/70 mb-4 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: event.description }}
                      />
                    )}
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center gap-3 text-[#3D3D3D]/70 font-sans text-sm">
                        <Calendar className="w-4 h-4 text-[#D4A5A5]" />
                        <span>{event.startDate} {event.startTime && `• ${event.startTime}`}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-3 text-[#3D3D3D]/70 font-sans text-sm">
                          <MapPin className="w-4 h-4 text-[#D4A5A5]" />
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>
                    {event.eventType === 'workshop' && (
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 mb-4">
                        <div className="flex items-center gap-2">
                          <Ticket className="w-5 h-5 text-[#C9A962]" />
                          <span className="font-sans font-semibold text-[#3D3D3D]">{event.isDonation ? "Choose Your Price" : `${event.price}`}</span>
                        </div>
                        {event.capacity !== undefined && (
                          <div className="flex items-center gap-2 text-[#3D3D3D]/60">
                            <Users className="w-4 h-4" />
                            <span className="font-sans text-sm">{event.capacity} spots available</span>
                          </div>
                        )}
                      </div>
                    )}
                    <Link to={`/events/${event.squareProductId}`} state={{ event }}>
                      <button className="w-full bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white rounded-full py-6 font-sans">{event.eventType === 'workshop' ? 'Get Tickets' : 'Learn More'}</button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <WorkshopContactSection />
    </div>
  );
}
